"use client";

import { Button } from "@/components/ui/Button";
import { DismissibleSuggestion } from "@/components/ui/DismissibleSuggestion";
import { Panel, PanelGrid } from "@/components/ui/Panel";
import { TransactionTypePill } from "@/components/portfolio/transactions/TransactionTypePill";
import { getPastUnverifiedTransactionPeriods } from "@/components/portfolio/transactions/transactionVerification";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { useAppContext } from "@/context/AppContext";
import { useEstateContext } from "@/context/EstateContext";
import { useLanguage } from "@/context/LanguageContext";
import {
  SuggestedTransactionPreference,
  Transaction,
  TransactionPeriodVerification,
  TransactionPreset,
  UserActivity,
} from "@/types";
import { formatCZK } from "@/utils/MyEstateHelper";
import {
  CheckCircleIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import { PlusIcon } from "@heroicons/react/24/solid";
import React from "react";

interface TransactionRowProps {
  transactions: Transaction[];
  activities: UserActivity[];
  hasLoadedActivity: boolean;
  hasActivityError: boolean;
}

type PendingReminder = {
  transaction: Transaction;
  period: string;
};

type PendingVerification = PendingReminder & {
  pastVerifications: TransactionPeriodVerification[];
};

function formatReminderPeriod(period: string) {
  const [month, year] = period.split(" ").map(Number);
  if (!month || !year) return period;

  return new Intl.DateTimeFormat("cs-CZ", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, 1)));
}

function formatVerificationPeriod(period: string, language: "cs" | "en") {
  const [month, year] = period.split(" ").map(Number);
  if (!month || !year) return period;

  return new Intl.DateTimeFormat(language === "cs" ? "cs-CZ" : "en-GB", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, 1)));
}

function reminderActivityKey(transactionId: number, period: string) {
  return `${transactionId}-${period}`;
}

const TransactionRow: React.FC<TransactionRowProps> = ({
  transactions,
  activities,
  hasLoadedActivity,
  hasActivityError,
}) => {
  const { language } = useLanguage();
  const copy = {
    cs: {
      prediction: "predikce",
      noTransactions: "Zatím zde nejsou žádné transakce",
      suggestions: "Doporučeno",
      addRent: "Přidat nájem",
      rent: "Nájem",
      addWater: "Přidat vodu",
      water: "Voda",
      addElectricity: "Přidat elektřinu",
      electricity: "Elektřina",
      addAdvancePayments: "Přidat zálohy",
      advancePayments: "Zálohy",
      addInternet: "Přidat internet",
      internet: "Internet",
      addInsurance: "Přidat pojištění",
      insurance: "Pojištění",
      hideSuggestion: "Skrýt toto doporučení",
      unverified: "Neověřeno",
      confirm: "Potvrdit",
      confirmed: "Ověřeno",
      verifyPastTitle: "Potvrdit i předchozí platby?",
      verifyPastDescription: "{name} · {count} nepotvrzených plateb",
      verifyOnlyThisMonth: "Jen {period}",
      verifyAll: "Potvrdit vše ({count})",
      confirmIncomeTooltip: "Potvrďte, že platba byla skutečně přijata.",
      confirmExpenseTooltip: "Potvrďte, že platba byla skutečně odeslána.",
      sendReminder: "Poslat připomínku",
      resendReminder: "Znovu poslat připomínku",
      sendReminderTooltip: "Poslat nájemci připomínku platby e-mailem.",
      resendReminderTooltip: "Znovu poslat nájemci připomínku platby.",
      missingTenantEmailTooltip: "Nejdříve doplňte e-mail nájemce.",
      loadingReminderHistory: "Načítám historii odeslaných připomínek.",
      reminderHistoryError:
        "Historii připomínek se nepodařilo načíst. Obnovte stránku.",
      cooldown: "Další připomínku lze odeslat až {date}.",
      reminderDialogTitle: "Odeslat připomínku",
      reminderDialogDescription: "Náhled připomínky platby",
      recipient: "Komu",
      subject: "Předmět",
      emailText: "Text e-mailu",
      cancel: "Zrušit",
      confirmSend: "Potvrdit a odeslat",
      sending: "Odesílám…",
      recurrence: {
        MONTHLY: "měsíčně",
        YEARLY: "ročně",
        ONCE: "jednorázově",
      },
    },
    en: {
      prediction: "forecast",
      noTransactions: "There are no transactions here yet",
      suggestions: "Suggested",
      addRent: "Add rent",
      rent: "Rent",
      addWater: "Add water bill",
      water: "Water bill",
      addElectricity: "Add electricity bill",
      electricity: "Electricity bill",
      addAdvancePayments: "Add advance payments",
      advancePayments: "Advance payments",
      addInternet: "Add internet",
      internet: "Internet",
      addInsurance: "Add insurance",
      insurance: "Insurance",
      hideSuggestion: "Hide this suggestion",
      unverified: "Unverified",
      confirm: "Confirm",
      confirmed: "Verified",
      verifyPastTitle: "Confirm previous payments too?",
      verifyPastDescription: "{name} · {count} unconfirmed payments",
      verifyOnlyThisMonth: "Only {period}",
      verifyAll: "Confirm all ({count})",
      confirmIncomeTooltip: "Confirm that the payment was actually received.",
      confirmExpenseTooltip: "Confirm that the payment was actually sent.",
      sendReminder: "Send reminder",
      resendReminder: "Send reminder again",
      sendReminderTooltip: "Send the tenant a payment reminder by email.",
      resendReminderTooltip: "Send the tenant another payment reminder.",
      missingTenantEmailTooltip: "Add the tenant's email address first.",
      loadingReminderHistory: "Loading sent reminder history.",
      reminderHistoryError:
        "Reminder history could not be loaded. Refresh the page.",
      cooldown: "Another reminder can be sent after {date}.",
      reminderDialogTitle: "Send reminder",
      reminderDialogDescription: "Payment reminder preview",
      recipient: "To",
      subject: "Subject",
      emailText: "Email content",
      cancel: "Cancel",
      confirmSend: "Confirm and send",
      sending: "Sending…",
      recurrence: {
        MONTHLY: "monthly",
        YEARLY: "yearly",
        ONCE: "one-time",
      },
    },
  }[language];
  const { userInfoGlobal, userPreferences, updateUserPreferences } =
    useAppContext();
  const ownerName = userInfoGlobal?.userName?.trim();
  const {
    openEditModalTransactions,
    selectedEstate,
    sendTransactionReminder,
    verifyTransactionPeriods,
  } = useEstateContext();
  const [verifyingPeriods, setVerifyingPeriods] = React.useState<Set<string>>(
    new Set(),
  );
  const [sendingReminderPeriods, setSendingReminderPeriods] = React.useState<
    Set<string>
  >(new Set());
  const [pendingReminder, setPendingReminder] =
    React.useState<PendingReminder | null>(null);
  const [pendingVerification, setPendingVerification] =
    React.useState<PendingVerification | null>(null);
  const [currentTime, setCurrentTime] = React.useState(() => Date.now());
  const latestReminderByKey = React.useMemo(() => {
    const latestByKey = new Map<string, UserActivity>();

    activities.forEach((activity) => {
      if (
        activity.type !== "TRANSACTION_REMINDER_EMAIL" ||
        activity.metadata.transactionId == null ||
        !activity.metadata.period
      ) {
        return;
      }

      const key = reminderActivityKey(
        Number(activity.metadata.transactionId),
        activity.metadata.period,
      );
      const current = latestByKey.get(key);
      if (
        !current ||
        new Date(activity.createdAt).getTime() >
          new Date(current.createdAt).getTime()
      ) {
        latestByKey.set(key, activity);
      }
    });

    return latestByKey;
  }, [activities]);

  React.useEffect(() => {
    const interval = window.setInterval(
      () => setCurrentTime(Date.now()),
      60_000,
    );
    return () => window.clearInterval(interval);
  }, []);
  const estatePreferenceKey = selectedEstate ? String(selectedEstate.id) : null;
  const hiddenSuggestions = estatePreferenceKey
    ? (userPreferences.hideSuggestedBox[estatePreferenceKey] ?? [])
    : [];
  const suggestions: Array<{
    key: SuggestedTransactionPreference;
    label: string;
    keywords: string[];
    preset: TransactionPreset;
  }> = [
    {
      key: "rent",
      label: copy.addRent,
      keywords: ["nájem", "rent"],
      preset: {
        name: copy.rent,
        transactionType: "INCOME",
        recurrence: "MONTHLY",
        showInDashboard: true,
      },
    },
    {
      key: "water",
      label: copy.addWater,
      keywords: ["voda", "vodné", "stočné", "water"],
      preset: {
        name: copy.water,
        transactionType: "EXPENSE",
        recurrence: "MONTHLY",
        showInDashboard: false,
      },
    },
    {
      key: "electricity",
      label: copy.addElectricity,
      keywords: ["elektřina", "energie", "electricity", "power"],
      preset: {
        name: copy.electricity,
        transactionType: "EXPENSE",
        recurrence: "MONTHLY",
        showInDashboard: false,
      },
    },
    {
      key: "internet",
      label: copy.addInternet,
      keywords: ["internet", "wifi"],
      preset: {
        name: copy.internet,
        transactionType: "EXPENSE",
        recurrence: "MONTHLY",
        showInDashboard: false,
      },
    },
    {
      key: "advancePayments",
      label: copy.addAdvancePayments,
      keywords: ["záloha", "zálohy", "předpis záloh", "advance payment"],
      preset: {
        name: copy.advancePayments,
        transactionType: "EXPENSE",
        recurrence: "MONTHLY",
        showInDashboard: false,
      },
    },
    {
      key: "insurance",
      label: copy.addInsurance,
      keywords: ["pojištění", "pojistné", "insurance"],
      preset: {
        name: copy.insurance,
        transactionType: "EXPENSE",
        recurrence: "YEARLY",
        showInDashboard: true,
      },
    },
  ];
  const normalizeName = (value: string) =>
    value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLocaleLowerCase();
  const availableSuggestions = selectedEstate
    ? suggestions.filter(
        ({ key, keywords, preset }) =>
          !hiddenSuggestions.includes(key) &&
          !(selectedEstate.transactions ?? []).some(
            (transaction) =>
              transaction.transactionType === preset.transactionType &&
              keywords.some((keyword) =>
                normalizeName(transaction.name).includes(
                  normalizeName(keyword),
                ),
              ),
          ),
      )
    : [];

  const hideSuggestion = (key: SuggestedTransactionPreference) => {
    if (!estatePreferenceKey) return;

    void updateUserPreferences({
      hideSuggestedBox: {
        ...userPreferences.hideSuggestedBox,
        [estatePreferenceKey]: hiddenSuggestions.includes(key)
          ? hiddenSuggestions
          : [...hiddenSuggestions, key],
      },
    });
  };

  const verificationPeriodForDate = (date: string) => {
    const transactionDate = new Date(date);
    return `${String(transactionDate.getMonth() + 1).padStart(2, "0")} ${transactionDate.getFullYear()}`;
  };

  const verifyTransactions = async (
    verifications: TransactionPeriodVerification[],
  ) => {
    const verificationKeys = verifications.map(
      ({ transactionId, period }) => `${transactionId}-${period}`,
    );
    setVerifyingPeriods((current) => {
      const next = new Set(current);
      verificationKeys.forEach((key) => next.add(key));
      return next;
    });
    try {
      return await verifyTransactionPeriods(verifications);
    } finally {
      setVerifyingPeriods((current) => {
        const next = new Set(current);
        verificationKeys.forEach((key) => next.delete(key));
        return next;
      });
    }
  };

  const requestTransactionVerification = (
    transaction: Transaction,
    period: string,
  ) => {
    if (!transaction.originalId) return;

    const pastVerifications = getPastUnverifiedTransactionPeriods(
      selectedEstate?.transactions ?? [],
      transaction,
      period,
    );

    if (pastVerifications.length === 0) {
      void verifyTransactions([
        { transactionId: transaction.originalId, period },
      ]);
      return;
    }

    setPendingVerification({ transaction, period, pastVerifications });
  };

  const confirmPendingVerification = async (includePast: boolean) => {
    if (!pendingVerification?.transaction.originalId) return;

    const currentVerification = {
      transactionId: pendingVerification.transaction.originalId,
      period: pendingVerification.period,
    };
    const wasVerified = await verifyTransactions(
      includePast
        ? [...pendingVerification.pastVerifications, currentVerification]
        : [currentVerification],
    );

    if (wasVerified) setPendingVerification(null);
  };

  const sendReminder = async (transaction: Transaction, period: string) => {
    if (!transaction.originalId || !selectedEstate?.tenantEmail?.trim()) return;
    const reminderKey = `${transaction.originalId}-${period}`;
    setSendingReminderPeriods((current) => new Set(current).add(reminderKey));
    try {
      await sendTransactionReminder(transaction.originalId, period);
    } finally {
      setSendingReminderPeriods((current) => {
        const next = new Set(current);
        next.delete(reminderKey);
        return next;
      });
    }
  };

  const confirmReminder = async () => {
    if (!pendingReminder) return;
    await sendReminder(pendingReminder.transaction, pendingReminder.period);
    setPendingReminder(null);
  };

  const reminderPeriodLabel = pendingReminder
    ? formatReminderPeriod(pendingReminder.period)
    : "";
  const reminderSubject = reminderPeriodLabel
    ? `Připomínka platby – ${reminderPeriodLabel}`
    : "";
  const pendingReminderKey = pendingReminder
    ? `${pendingReminder.transaction.originalId}-${pendingReminder.period}`
    : "";
  const isSendingPendingReminder =
    sendingReminderPeriods.has(pendingReminderKey);
  const pendingVerificationKey = pendingVerification
    ? `${pendingVerification.transaction.originalId}-${pendingVerification.period}`
    : "";
  const isVerifyingPending = verifyingPeriods.has(pendingVerificationKey);
  const pendingVerificationPeriodLabel = pendingVerification
    ? formatVerificationPeriod(pendingVerification.period, language)
    : "";

  return (
    <div>
      {availableSuggestions.length > 0 && (
        <Panel className="m-4 bg-white/55 p-3 text-left dark:bg-white/[0.04] sm:mx-6">
          <p className="mb-2 text-[9px] font-medium uppercase tracking-[0.14em] text-softiq-primary/40 dark:text-white/40">
            {copy.suggestions}
          </p>
          <div className="flex flex-wrap gap-2">
            {availableSuggestions.map(({ key, label, preset }) => (
              <DismissibleSuggestion
                key={key}
                onClick={() => openEditModalTransactions(preset)}
                onDismiss={() => hideSuggestion(key)}
                dismissLabel={copy.hideSuggestion}
                icon={<PlusIcon className="size-3.5" />}
              >
                {label}
              </DismissibleSuggestion>
            ))}
          </div>
        </Panel>
      )}

      {transactions.length > 0 ? (
        <PanelGrid
          className={`app-transaction-list ${
            availableSuggestions.length > 0
              ? "border-t border-softiq-primary/10 dark:border-white/10"
              : ""
          }`}
        >
          {[...transactions]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((t) => {
              const verificationPeriod = verificationPeriodForDate(t.dateFrom);
              const verificationKey = `${t.originalId}-${verificationPeriod}`;
              const verifiedPeriods =
                t.verification?.listOfVerifiedTransactions ?? [];
              const canConfirmTransaction = t.transactionType === "INCOME";
              const requiresVerification =
                !t.isPrediction &&
                canConfirmTransaction &&
                !verifiedPeriods.includes(verificationPeriod);
              const isVerified =
                !t.isPrediction &&
                canConfirmTransaction &&
                verifiedPeriods.includes(verificationPeriod);
              const isVerifying = verifyingPeriods.has(verificationKey);
              const isSendingReminder =
                sendingReminderPeriods.has(verificationKey);
              const hasTenantEmail = Boolean(
                selectedEstate?.tenantEmail?.trim(),
              );
              const lastReminder = latestReminderByKey.get(verificationKey);
              const lastReminderTime = lastReminder
                ? new Date(lastReminder.createdAt).getTime()
                : null;
              const reminderCooldownUntil =
                lastReminderTime !== null
                  ? lastReminderTime + 24 * 60 * 60 * 1000
                  : null;
              const isReminderOnCooldown =
                reminderCooldownUntil !== null &&
                reminderCooldownUntil > currentTime;
              const reminderButtonLabel = lastReminder
                ? copy.resendReminder
                : copy.sendReminder;
              const reminderTooltip = !hasLoadedActivity
                ? copy.loadingReminderHistory
                : hasActivityError
                  ? copy.reminderHistoryError
                  : !hasTenantEmail
                    ? copy.missingTenantEmailTooltip
                    : isReminderOnCooldown && reminderCooldownUntil !== null
                      ? copy.cooldown.replace(
                          "{date}",
                          new Intl.DateTimeFormat(
                            language === "cs" ? "cs-CZ" : "en-GB",
                            {
                              dateStyle: "medium",
                              timeStyle: "short",
                            },
                          ).format(new Date(reminderCooldownUntil)),
                        )
                      : lastReminder
                        ? copy.resendReminderTooltip
                        : copy.sendReminderTooltip;

              return (
                <div key={t.id} className="app-transaction-item">
                  <Panel
                    variant="row"
                    className={`app-transaction-row ${
                      t.showInDashboard
                        ? "text-foreground/70"
                        : "text-foreground"
                    }`}
                  >
                  <div className="app-transaction-row__name">
                    <p className="truncate text-xs font-medium text-softiq-primary dark:text-white">
                      {t.name}
                    </p>
                    <div className="app-transaction-row__meta">
                      <TransactionTypePill type={t.transactionType} />
                      {t.isPrediction ? (
                        <span>{copy.prediction}</span>
                      ) : requiresVerification && canConfirmTransaction ? (
                        <span className="app-transaction-verification-status app-transaction-verification-status--pending">
                          <ExclamationCircleIcon className="size-3.5" />
                          {copy.unverified}
                        </span>
                      ) : isVerified ? (
                        <span className="app-transaction-verification-status app-transaction-verification-status--verified">
                          <CheckCircleIcon className="size-3.5" />
                          {copy.confirmed}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="app-transaction-row__amount">
                    <p
                      className={`text-sm font-medium ${
                        t.transactionType === "INCOME"
                          ? "text-softiq-secondary-shade-700 dark:text-softiq-secondary"
                          : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {t.transactionType === "INCOME" ? "+" : "-"}
                      {formatCZK(Number(t.amount))}
                    </p>
                    <p className="app-section-meta lg:mt-1">
                      {copy.recurrence[
                        t.recurrence as keyof typeof copy.recurrence
                      ] ?? t.recurrence}
                    </p>
                  </div>
                  </Panel>
                  {requiresVerification && canConfirmTransaction ? (
                    <Panel
                      variant="row"
                      className="app-transaction-actions-card"
                    >
                          <TooltipProvider delayDuration={0}>
                            <div className="flex items-center gap-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex">
                                    <Button
                                      type="button"
                                      variant="success"
                                      size="sm"
                                      disabled={isVerifying}
                                      onClick={() =>
                                        requestTransactionVerification(
                                          t,
                                          verificationPeriod,
                                        )
                                      }
                                    >
                                      <CheckCircleIcon className="size-3.5" />
                                      {copy.confirm}
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" sideOffset={15}>
                                  {t.transactionType === "INCOME"
                                    ? copy.confirmIncomeTooltip
                                    : copy.confirmExpenseTooltip}
                                </TooltipContent>
                              </Tooltip>

                              <div className="relative flex items-center">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex">
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        disabled={
                                          !hasLoadedActivity ||
                                          hasActivityError ||
                                          !hasTenantEmail ||
                                          isSendingReminder ||
                                          isReminderOnCooldown
                                        }
                                        aria-label={reminderButtonLabel}
                                        onClick={() =>
                                          setPendingReminder({
                                            transaction: t,
                                            period: verificationPeriod,
                                          })
                                        }
                                      >
                                        <EnvelopeIcon className="size-3.5" />
                                        <span>{reminderButtonLabel}</span>
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" sideOffset={15}>
                                    {reminderTooltip}
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </div>
                          </TooltipProvider>
                    </Panel>
                  ) : null}
                </div>
              );
            })}
        </PanelGrid>
      ) : (
        <div className="flex min-h-24 flex-col items-center justify-center px-5 py-6 text-center sm:px-6">
          <p className="app-meta-muted text-xs">{copy.noTransactions}</p>
        </div>
      )}

      <Dialog
        open={pendingVerification !== null}
        onOpenChange={(open) => {
          if (!open && !isVerifyingPending) setPendingVerification(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="app-dialog-title">
              {copy.verifyPastTitle}
            </DialogTitle>
            {pendingVerification ? (
              <DialogDescription>
                {copy.verifyPastDescription
                  .replace("{name}", pendingVerification.transaction.name)
                  .replace(
                    "{count}",
                    String(pendingVerification.pastVerifications.length),
                  )}
              </DialogDescription>
            ) : null}
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isVerifyingPending}
              onClick={() => void confirmPendingVerification(false)}
            >
              {copy.verifyOnlyThisMonth.replace(
                "{period}",
                pendingVerificationPeriodLabel,
              )}
            </Button>
            <Button
              type="button"
              variant="success"
              loading={isVerifyingPending}
              onClick={() => void confirmPendingVerification(true)}
            >
              {copy.verifyAll.replace(
                "{count}",
                String(
                  (pendingVerification?.pastVerifications.length ?? 0) + 1,
                ),
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingReminder !== null}
        onOpenChange={(open) => {
          if (!open && !isSendingPendingReminder) setPendingReminder(null);
        }}
      >
        <DialogContent className="max-w-2xl gap-0 overflow-hidden p-0">
          <DialogHeader className="border-b border-border/70 bg-hover/60 px-5 py-4 pr-12 sm:px-6">
            <DialogTitle className="app-dialog-title">
              {copy.reminderDialogTitle}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {copy.reminderDialogDescription}
            </DialogDescription>
          </DialogHeader>

          {pendingReminder && selectedEstate ? (
            <div className="space-y-5 px-5 py-5 sm:px-6">
              <dl className="divide-y divide-softiq-primary/10 border-y border-softiq-primary/10 dark:divide-white/10 dark:border-white/10">
                <div className="grid gap-1 py-3 sm:grid-cols-[7rem_minmax(0,1fr)] sm:gap-4">
                  <dt className="app-section-label">{copy.recipient}</dt>
                  <dd className="min-w-0 break-all text-sm font-medium text-foreground">
                    {selectedEstate.tenantEmail}
                  </dd>
                </div>
                <div className="grid gap-1 py-3 sm:grid-cols-[7rem_minmax(0,1fr)] sm:gap-4">
                  <dt className="app-section-label">{copy.subject}</dt>
                  <dd className="text-sm font-medium text-foreground">
                    {reminderSubject}
                  </dd>
                </div>
              </dl>

              <div>
                <p className="app-section-label mb-2">{copy.emailText}</p>
                <div className="app-email-preview">
                  <div className="app-email-preview__header">
                    <p className="app-email-preview__eyebrow">
                      Připomínka platby
                    </p>
                    <p className="mt-1.5 text-lg font-medium">
                      {reminderSubject}
                    </p>
                  </div>
                  <div className="space-y-4 px-5 py-5 text-sm leading-6 sm:px-6">
                    <p>Dobrý den,</p>
                    <p className="app-email-preview__muted">
                      stále neevidujeme příchozí platbu. Prosíme, ověřte, zda
                      byla platba z vaší strany odeslána. Pokud již byla
                      odeslána, považujte prosím tuto zprávu za bezpředmětnou.
                    </p>
                    {ownerName ? (
                      <p className="app-email-preview__meta">
                        Pronajímatel:{" "}
                        <span className="font-medium text-softiq-primary">
                          {ownerName}
                        </span>
                      </p>
                    ) : null}
                    <dl className="app-email-preview__details">
                      {[
                        ["Nemovitost", selectedEstate.name],
                        ["Platba", pendingReminder.transaction.name],
                        ["Období", reminderPeriodLabel],
                      ].map(([label, value]) => (
                        <div
                          key={label}
                          className="flex items-center justify-between gap-4 px-4 py-2.5"
                        >
                          <dt className="app-email-preview__meta">{label}</dt>
                          <dd className="text-right text-xs font-medium">
                            {value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                    <p className="app-email-preview__meta">
                      Na tento e-mail můžete odpovědět přímo pronajímateli.
                    </p>
                  </div>
                  <div className="app-email-preview__footer">
                    Odesláno prostřednictvím SOFTIQ
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <DialogFooter className="border-t border-border/70 bg-hover/35 px-5 py-3 sm:px-6">
            <Button
              type="button"
              variant="outline"
              size="compact"
              disabled={isSendingPendingReminder}
              onClick={() => setPendingReminder(null)}
            >
              {copy.cancel}
            </Button>
            <Button
              type="button"
              size="compact"
              loading={isSendingPendingReminder}
              loadingLabel={copy.sending}
              onClick={() => void confirmReminder()}
            >
              {copy.confirmSend}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransactionRow;
